import { useEffect, useMemo, useRef, useState } from "react";

import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import {
  Badge,
  DataRow,
  DataTable,
  LoadingOverlay,
  PaginationFooter,
  SearchField,
  buildPageList,
} from "@breakingthelines/design-system";

import { openObserveViews } from "@/config/env";
import { listAuditEvents, type AuditEvent, type PageInfo } from "@/lib/admin-api";

import styles from "./audit-logs.module.css";

// This page shows admin-service's audit trail: one row per admin mutation it
// served. Reads are not recorded, so nothing here is a page view.
//
// It used to render nine identical placeholder rows ("Timestamp", "Action",
// "Entity", @gabrielfoligno nine times) over a dead search box, three dead
// filter buttons, a hardcoded "Total Logs: 240" and page numbers up to 68. All
// of it is gone. A control that reaches nothing is worse than no control: it
// reads as a search that found nothing.

const PER_PAGE_OPTIONS = [10, 20, 50];

// Actions are shown and filtered by the exact string admin-service records, not
// a prettified label. That string is what the same event carries in
// OpenObserve, so an operator who finds a row here can grep for it there; a
// display map would also go stale the next time a route is added.
//
// The list mirrors auditRoutes in admin-service internal/api/httpapi/audit.go.
// A value missing from here is still searchable through the query box, and
// still renders in the table.
const ACTION_OPTIONS = [
  { value: "all", label: "All" },
  { value: "admin.grant", label: "admin.grant" },
  { value: "admin.revoke", label: "admin.revoke" },
  { value: "user.ban", label: "user.ban" },
  { value: "user.unban", label: "user.unban" },
  { value: "user.reset_handle", label: "user.reset_handle" },
  { value: "user.set_verified", label: "user.set_verified" },
  { value: "content.flag", label: "content.flag" },
  { value: "moderation.moderate_content", label: "moderation.moderate_content" },
  { value: "moderation.review_content_flag", label: "moderation.review_content_flag" },
  { value: "moderation.review_thought_flag", label: "moderation.review_thought_flag" },
  { value: "moderation.cascade_hide_user", label: "moderation.cascade_hide_user" },
  { value: "membership.grant", label: "membership.grant" },
  { value: "membership.revoke", label: "membership.revoke" },
  { value: "membership.set_expiry", label: "membership.set_expiry" },
  { value: "editorial_override.create", label: "editorial_override.create" },
  { value: "editorial_override.delete", label: "editorial_override.delete" },
  { value: "authorized_publisher.add", label: "authorized_publisher.add" },
  { value: "authorized_publisher.remove", label: "authorized_publisher.remove" },
  { value: "advertiser.create", label: "advertiser.create" },
  { value: "advertiser.issue_recovery_link", label: "advertiser.issue_recovery_link" },
  { value: "advertiser_grant.create", label: "advertiser_grant.create" },
  { value: "advertiser_grant.revoke", label: "advertiser_grant.revoke" },
  { value: "advertiser_grant.adjust", label: "advertiser_grant.adjust" },
  { value: "advertiser_content.unlock_revision", label: "advertiser_content.unlock_revision" },
  { value: "advertiser_squad.provision", label: "advertiser_squad.provision" },
  { value: "archetype.save_draft", label: "archetype.save_draft" },
  { value: "archetype.publish", label: "archetype.publish" },
  { value: "archetype.retire", label: "archetype.retire" },
] as const;

const ENTITY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "user", label: "user" },
  { value: "content", label: "content" },
  { value: "membership", label: "membership" },
  { value: "publisher", label: "publisher" },
  { value: "principal", label: "principal" },
  { value: "advertiser_grant", label: "advertiser_grant" },
  { value: "editorial_override", label: "editorial_override" },
  { value: "squad", label: "squad" },
  { value: "content_flag", label: "content_flag" },
  { value: "thought_flag", label: "thought_flag" },
  { value: "archetype", label: "archetype" },
] as const;

// The placeholder toolbar used to offer a dead "Date Range" filter button.
// admin-service#67 adds real from/to query params, so this page now has two
// date inputs that reach them. `from`/`to` are sent as bare YYYY-MM-DD, what
// an <input type="date"> gives natively: the server treats `from` as the
// start of that UTC day and `to` as covering the whole named day, not just
// its midnight instant, so picking the same day in both fields returns that
// day's events.

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

// Audit rows need the time of day, unlike every other list in this dashboard:
// "who did what, in what order, on the afternoon of the 3rd" is the question
// being asked. 24-hour and UTC, because the recorded instant is UTC and a
// browser-local rendering would put two operators in different timezones on
// different days.
function formatTimestamp(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  // en-US matches the month spelling every other list page uses ("Sep", where
  // en-GB gives "Sept"); the hour and minute are the part this page adds.
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(date);
}

function actorLabel(event: AuditEvent): string {
  if (event.actorUsername) return `@${event.actorUsername}`;
  if (event.actorDisplayName) return event.actorDisplayName;
  return event.actorId;
}

type FilterOption = { readonly value: string; readonly label: string };

function FilterDropdown({
  name,
  options,
  value,
  onSelect,
}: {
  name: string;
  options: readonly FilterOption[];
  value: string;
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const label = value === "all" ? name : `${name}: ${value}`;

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.dropdown} ${value !== "all" ? styles.dropdownActive : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.dropdownLabel}>{label}</span>
        <CaretDown className={styles.dropdownIcon} size={12} />
      </button>
      {open ? (
        <div className={styles.dropdownMenu} role="listbox" aria-label={name}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`${styles.dropdownOption} ${option.value === value ? styles.dropdownOptionActive : ""}`}
              onClick={() => {
                onSelect(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AuditLogsPage() {
  const [searchInput, setSearchInput] = useState("");
  const query = useDebouncedValue(searchInput.trim());
  const [action, setAction] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Page 4 of the old filter names nothing in the new one.
  useEffect(() => {
    setPageIndex(1);
  }, [query, action, entityType, fromDate, toDate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listAuditEvents({
      query,
      action: action === "all" ? undefined : action,
      entityType: entityType === "all" ? undefined : entityType,
      from: fromDate || undefined,
      to: toDate || undefined,
      pageSize,
      page: pageIndex,
    })
      .then((response) => {
        if (cancelled) return;
        setEvents(response.events ?? []);
        setPageInfo(response.pageInfo ?? null);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Could not load the logs.");
        setEvents([]);
        setPageInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, action, entityType, fromDate, toDate, pageIndex, pageSize]);

  // Computed once per render, not stateful: it is a pure function of build-time
  // config (src/config/env.ts), so there is nothing to load or fail.
  const ooViews = openObserveViews();

  // Falling back to the loaded row count keeps the label honest when the
  // service omits page_info rather than inventing a number.
  const totalCount = pageInfo?.totalCount ?? events.length;

  const totalPages = useMemo(() => {
    if (totalCount > 0) return Math.max(1, Math.ceil(totalCount / pageSize));
    return pageIndex;
  }, [totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasNext = pageInfo?.hasNextPage ?? (events.length === pageSize && events.length > 0);

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Logs</h1>
        {/* One clause of scope. The page had no line at all, and "reads are
            not recorded" is the fact an operator needs before reading a gap
            here as nothing having happened. */}
        <p className={styles.subtitle}>Every change an admin made. Reads are not recorded.</p>
        <div className={styles.toolbar}>
          <SearchField
            className={styles.searchField}
            icon={<MagnifyingGlass size={14} />}
            label="Search audit logs"
            placeholder="Admin, action, entity or reason"
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
          />
          <div className={styles.filters}>
            <FilterDropdown
              name="Action"
              options={ACTION_OPTIONS}
              value={action}
              onSelect={setAction}
            />
            <FilterDropdown
              name="Entity"
              options={ENTITY_OPTIONS}
              value={entityType}
              onSelect={setEntityType}
            />
            <div className={styles.dateRange}>
              <input
                type="date"
                className={styles.dateInput}
                aria-label="From date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(event) => setFromDate(event.currentTarget.value)}
              />
              <span className={styles.dateRangeSeparator} aria-hidden>
                to
              </span>
              <input
                type="date"
                className={styles.dateInput}
                aria-label="To date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(event) => setToDate(event.currentTarget.value)}
              />
              {fromDate || toDate ? (
                <button
                  type="button"
                  className={styles.clearDates}
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                >
                  Clear dates
                </button>
              ) : null}
            </div>
          </div>
        </div>
        {ooViews ? (
          <div className={styles.ooLinks}>
            <a
              href={ooViews.adminMutations}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ooLink}
            >
              Admin changes in OpenObserve
            </a>
            <a
              href={ooViews.serviceLogs}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ooLink}
            >
              Service logs in OpenObserve
            </a>
          </div>
        ) : null}
      </div>

      <section className={styles.tableSection}>
        {loading ? <LoadingOverlay label="Loading logs" /> : null}

        <DataTable
          columns="minmax(150px, 1fr) minmax(160px, 1fr) minmax(180px, 1.2fr) minmax(140px, 1fr) minmax(160px, 1.4fr)"
          header={["Timestamp", "Admin", "Action", "Entity", "Reason"]}
          headerClassName={styles.tableHeader}
        >
          {events.map((event) => (
            <DataRow key={event.id} className={styles.tableRow}>
              <span className={styles.meta} data-label="Timestamp">
                {formatTimestamp(event.occurredAt)}
              </span>
              <span className={styles.adminCell} data-label="Admin">
                <span className={styles.avatar} aria-hidden />
                {actorLabel(event)}
              </span>
              <span className={styles.actionCell} data-label="Action">
                <span className={styles.meta}>{event.action}</span>
                {event.outcome === "failure" ? (
                  <Badge variant="tintedWarn">{`Failed ${event.statusCode}`}</Badge>
                ) : null}
              </span>
              <span className={styles.entityCell} data-label="Entity">
                {event.entityType ? (
                  <span className={styles.entityType}>{event.entityType}</span>
                ) : null}
                {event.entityId ? <span className={styles.meta}>{event.entityId}</span> : null}
              </span>
              <span className={styles.meta} data-label="Reason">
                {event.reason}
              </span>
            </DataRow>
          ))}
        </DataTable>

        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && events.length === 0 ? (
          <div className={styles.emptyState}>No logs found.</div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={`Logs: ${totalCount}`}
        pages={pages}
        activePage={activePage}
        hasPrevious={pageIndex > 1}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((p) => Math.max(1, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.pagination}
      />
    </div>
  );
}
